import React, { useState } from 'react';
import { Button } from './Button';
import { Modal } from './Modal';
import FeatherIcon from 'feather-icons-react';

export interface BillToCompletedModalProps {
  isOpen: boolean;
  patientName: string;
  accClaimNumber: string;
  onYes: () => void;
  onNo: () => void;
  onCancel: () => void;
  isLoading?: boolean;
}

/**
 * BillToCompletedModal - AXIS076 AE Validation Workflow
 * 
 * First checkpoint modal for ACC Accredited Employer referrals.
 * Prompts staff to confirm billing information has been entered into Indici.
 * 
 * - Yes → Proceeds to AlertCompletedModal
 * - No → Proceeds to SendAccEmailModal
 */
export const BillToCompletedModal: React.FC<BillToCompletedModalProps> = ({
  isOpen,
  patientName,
  accClaimNumber,
  onYes,
  onNo,
  onCancel,
  isLoading = false,
}) => {
  const [copiedFeedback, setCopiedFeedback] = useState<string | null>(null);

  const templateSubject = `Billing Confirmation Required - ${patientName}`;
  const templateBody = `This is a reminder to confirm that billing information has been entered into Indici for ${patientName} (ACC# ${accClaimNumber}).

Please verify that all billing details are complete before proceeding.

[Note: This template content is pending business confirmation]`;

  const handleCopyToClipboard = async () => {
    try {
      const textToCopy = `Subject: ${templateSubject}\n\n${templateBody}`;
      
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(textToCopy);
      } else {
        // Fallback for older browsers
        const textArea = document.createElement('textarea');
        textArea.value = textToCopy;
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
      }
      
      setCopiedFeedback('✓ Template copied to clipboard');
      setTimeout(() => setCopiedFeedback(null), 3000);
    } catch (err) {
      console.error('Failed to copy template:', err);
      setCopiedFeedback('Failed to copy. Please select and copy manually.');
      setTimeout(() => setCopiedFeedback(null), 5000);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onCancel}
      isLoading={isLoading}
      style={{ maxWidth: '600px' }}
    >
      <div className="modal-header">
        <h3>Bill To Completed?</h3>
      </div>
      <div className="modal-body" style={{ overflowY: 'auto' }}>
        <p style={{ marginBottom: '0', marginTop: '0' }}>
          <strong>Patient:</strong> {patientName}
        </p>
        <p style={{ marginBottom: '12px', marginTop: '0' }}>
          <strong>ACC Claim Number:</strong> {accClaimNumber}
        </p>
        
        <div style={{ marginBottom: '12px' }}>
          <p style={{ marginBottom: '8px' }}>
            Has the billing information been entered into Indici for this ACC Accredited Employer referral?
          </p>
          <p style={{ fontSize: '14px', color: '#666', marginBottom: '0' }}>
            If not, you can copy the template email below to send to the billing team.
          </p>
        </div>

        {/* Template Email Section */}
        <div style={{ 
          backgroundColor: '#f5f5f5', 
          padding: '16px', 
          borderRadius: '8px',
          marginBottom: '16px'
        }}>
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            marginBottom: '12px'
          }}>
            <strong style={{ fontSize: '14px' }}>Template Email</strong>
            <Button
              variant="secondary"
              size="sm"
              onClick={handleCopyToClipboard}
              disabled={isLoading}
            >
              <FeatherIcon icon="copy" size={14} style={{ marginRight: '4px' }} />
              Copy to Clipboard
            </Button>
          </div>
          
          <div style={{ 
            backgroundColor: 'white', 
            padding: '12px', 
            borderRadius: '4px',
            border: '1px solid #ddd',
            fontSize: '13px',
            fontFamily: 'monospace',
            whiteSpace: 'pre-wrap',
            maxHeight: '200px',
            overflowY: 'auto'
          }}>
            <div style={{ marginBottom: '8px' }}>
              <strong>Subject:</strong> {templateSubject}
            </div>
            <div>
              {templateBody}
            </div>
          </div>
          
          {copiedFeedback && (
            <p style={{ 
              color: copiedFeedback.startsWith('✓') ? '#4caf50' : '#f44336',
              fontSize: '13px',
              marginTop: '8px',
              marginBottom: '0'
            }}>
              {copiedFeedback}
            </p>
          )}
        </div>
      </div>
      
      <div className="modal-footer" style={{ justifyContent: 'space-between' }}>
        <Button
          variant="secondary"
          onClick={onCancel}
          disabled={isLoading}
        >
          Cancel
        </Button>
        <div style={{ display: 'flex', gap: '8px' }}>
          <Button
            variant="warning"
            onClick={onNo}
            disabled={isLoading}
          >
            No - Not Complete
          </Button>
          <Button
            variant="success"
            onClick={onYes}
            disabled={isLoading}
            isLoading={isLoading}
          >
            Yes - Complete
          </Button>
        </div>
      </div>
    </Modal>
  );
};
